/**
 * Refined button component following design brief
 * Professional, responsive interaction states
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { theme } from '@core/theme';

export interface ButtonConfig {
  label: string;
  width?: number;
  height?: number;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_PRESETS = {
  sm: { width: 120, height: 40, fontSize: theme.typography.sizes.sm },
  md: { width: 160, height: 48, fontSize: theme.typography.sizes.base },
  lg: { width: 200, height: 56, fontSize: theme.typography.sizes.lg },
};

const VARIANT_STYLES = {
  primary: {
    bg: theme.colors.decision,
    bgHover: theme.colors.decisionHover,
    bgPress: theme.colors.decisionPress,
    text: theme.colors.textDark,
    border: theme.colors.decision,
  },
  secondary: {
    bg: theme.colors.cardBg,
    bgHover: theme.colors.cardBgHover,
    bgPress: theme.colors.neutralDark,
    text: theme.colors.textPrimary,
    border: theme.colors.neutral,
  },
  ghost: {
    bg: 0x00000000,
    bgHover: theme.colors.neutralDark,
    bgPress: theme.colors.neutral,
    text: theme.colors.textSecondary,
    border: 0x00000000,
  },
};

export class Button extends Container {
  private config: Required<Omit<ButtonConfig, 'size'>> & { size: 'sm' | 'md' | 'lg' };
  private background: Graphics;
  private labelText: Text;

  private isHovered = false;
  private isPressed = false;
  private isEnabled = true;
  private onClick: (() => void) | null = null;

  private targetY = 0;
  private currentY = 0;

  constructor(config: ButtonConfig) {
    super();

    const size = config.size || 'md';
    const preset = SIZE_PRESETS[size];

    this.config = {
      label: config.label,
      width: config.width || preset.width,
      height: config.height || preset.height,
      variant: config.variant || 'primary',
      size,
    };

    this.background = new Graphics();
    this.addChild(this.background);

    const variant = VARIANT_STYLES[this.config.variant];

    const textStyle = new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: preset.fontSize,
      fontWeight: theme.typography.weights.semibold,
      fill: variant.text,
      letterSpacing: theme.typography.letterSpacing.wide,
    });

    this.labelText = new Text({ text: this.config.label, style: textStyle });
    this.labelText.anchor.set(0.5);
    this.addChild(this.labelText);

    this.draw();
    this.setupInteraction();
  }

  private draw(): void {
    const { width, height, variant } = this.config;
    const styles = VARIANT_STYLES[variant];

    let bgColor = styles.bg;
    let borderColor = styles.border;

    if (!this.isEnabled) {
      bgColor = theme.colors.neutralDark;
      borderColor = theme.colors.neutralDark;
    } else if (this.isPressed) {
      bgColor = styles.bgPress;
      this.targetY = 1;
    } else if (this.isHovered) {
      bgColor = styles.bgHover;
      this.targetY = -2;
    } else {
      this.targetY = 0;
    }

    // Smooth Y position transition
    this.currentY += (this.targetY - this.currentY) * 0.3;

    this.background.clear();

    // Shadow (only for primary buttons)
    if (variant === 'primary' && this.isEnabled) {
      const shadowAlpha = this.isPressed ? 0.1 : this.isHovered ? 0.25 : 0.15;
      const shadowOffset = this.isPressed ? 2 : this.isHovered ? 6 : 4;
      this.background.roundRect(0, shadowOffset, width, height, theme.radii.md);
      this.background.fill({ color: 0x000000, alpha: shadowAlpha });
    }

    // Main background
    this.background.roundRect(0, this.currentY, width, height, theme.radii.md);
    this.background.fill(bgColor);

    // Border for secondary buttons
    if (variant === 'secondary') {
      this.background.roundRect(0, this.currentY, width, height, theme.radii.md);
      this.background.stroke({ width: 1, color: borderColor, alpha: 0.5 });
    }

    this.labelText.position.set(width / 2, height / 2 + this.currentY);

    // Update text color based on state
    if (!this.isEnabled) {
      this.labelText.style.fill = theme.colors.textMuted;
    } else {
      this.labelText.style.fill = styles.text;
    }
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerover', () => {
      if (!this.isEnabled) return;
      this.isHovered = true;
      this.animateDraw();
    });

    this.on('pointerout', () => {
      this.isHovered = false;
      this.isPressed = false;
      this.animateDraw();
    });

    this.on('pointerdown', () => {
      if (!this.isEnabled) return;
      this.isPressed = true;
      this.animateDraw();
    });

    this.on('pointerup', () => {
      if (this.isPressed && this.isEnabled) {
        this.isPressed = false;
        this.animateDraw();
        this.onClick?.();
      }
    });

    this.on('pointerupoutside', () => {
      this.isPressed = false;
      this.animateDraw();
    });
  }

  private animateDraw(): void {
    // Animate position changes smoothly
    const animate = () => {
      const diff = Math.abs(this.targetY - this.currentY);
      if (diff > 0.1) {
        this.draw();
        requestAnimationFrame(animate);
      } else {
        this.currentY = this.targetY;
        this.draw();
      }
    };
    requestAnimationFrame(animate);
  }

  setOnClick(callback: () => void): void {
    this.onClick = callback;
  }

  setLabel(text: string): void {
    this.config.label = text;
    this.labelText.text = text;
  }

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1 : 0.6;
    this.draw();
  }

  getDimensions(): { width: number; height: number } {
    return {
      width: this.config.width,
      height: this.config.height,
    };
  }
}
